import React, { Fragment } from "react";
import { Chart } from "react-google-charts";
import { Box, Typography, Grid, Paper } from "@mui/material";

const chartOptions = {
  chartArea: { width: "80%", height: "70%" },
  colors: ["#0AB2EE", "#FF6B6B"],
  legend: { position: "top", alignment: "center" },
  hAxis: {
    title: "Month",
    titleTextStyle: { color: "#666", fontSize: 12 },
  },
  vAxis: {
    title: "Number of Narrators",
    titleTextStyle: { color: "#666", fontSize: 12 },
    minValue: 0,
  },
  isStacked: true,
  backgroundColor: "transparent",
  titleTextStyle: {
    color: "#020303",
    fontSize: 16,
    bold: true,
  },
};

const NarratorsAnalytics = ({ narratorAnalytics, loading }) => {
  const { monthlyData, grandTotals } = narratorAnalytics;

  // Helper function to format month to YY-MM
  const formatMonthShort = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return `${year.slice(-2)}-${month}`;
  };

  // Helper function to format month for table display
  const formatMonthLong = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  // Prepare data for Google Charts
  const chartData = [
    ["Month", "User Narrators", "Non-User Narrators"],
    ...monthlyData.map(item => [
      formatMonthShort(item.month),
      item.users,
      item.nonUsers
    ])
  ];

  return (
    <Fragment>
      <Typography
        variant="h6"
        sx={{
          color: "#020303",
          fontWeight: 700,
          fontSize: 14,
          mb: 2,
        }}
      >
        Narrator Analytics by Month
      </Typography>

      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: "center", backgroundColor: "#f8f9fa" }}>
            <Typography variant="h4" sx={{ color: "#0AB2EE", fontWeight: "bold" }}>
              {grandTotals.users}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              User Narrators
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: "center", backgroundColor: "#f8f9fa" }}>
            <Typography variant="h4" sx={{ color: "#FF6B6B", fontWeight: "bold" }}>
              {grandTotals.nonUsers}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Non-User Narrators
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: "center", backgroundColor: "#f8f9fa" }}>
            <Typography variant="h4" sx={{ color: "#333", fontWeight: "bold" }}>
              {grandTotals.total}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Total Narrators
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 280 }}>
          <Typography>Loading narrator analytics...</Typography>
        </Box>
      ) : chartData.length > 1 ? (
        <Chart
          chartType="ColumnChart"
          width="100%"
          height="280px"
          data={chartData}
          options={chartOptions}
        />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 280 }}>
          <Typography sx={{ color: "#666" }}>No narrator data available</Typography>
        </Box>
      )}

      {/* Monthly Breakdown Table */}
      {monthlyData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
            Monthly Breakdown
          </Typography>
          <Paper elevation={1} sx={{ maxHeight: 200, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Month</th>
                  <th style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd" }}>User Narrators</th>
                  <th style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd" }}>Non-User Narrators</th>
                  <th style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #ddd" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData
                  .slice()
                  .sort((a, b) => b.month.localeCompare(a.month))
                  .map((item, index) => (
                  <tr key={item.month} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      {formatMonthLong(item.month)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #eee", color: "#0AB2EE" }}>
                      {item.users}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #eee", color: "#FF6B6B" }}>
                      {item.nonUsers}
                    </td>
                    <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #eee", fontWeight: "bold" }}>
                      {item.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </Box>
      )}
    </Fragment>
  );
};

export default NarratorsAnalytics;
